import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Text, Button } from 'components/ui';
import Icon from 'components/Icon';
import ProductImage from 'components/Product/ProductImage';
import AddToBasketButton from 'components/AddToBasketButton';
import { CARD_IMG_SIZE_GRID_XX_LARGE } from 'constants/productCard';
import helpersUtils from 'utils/Helpers';
import Storage from 'utils/localStorage/Storage';
import Constants from 'utils/localStorage/Constants';
import {
    colors, space, fontSizes, fontWeights, lineHeights
} from 'style/config';
import Empty from 'constants/empty';

const { formatDiscountPrice, appendDollarSign } = helpersUtils;

function VariationDetails({ product, extraCss = Empty.Object }) {
    const color = product?.color !== null && product.color;
    const size = product?.size;
    const variationDesc = product?.variationDesc;

    if (!color && !size) {
        return null;
    }

    return <Text css={{ ...styles.size, ...extraCss }}>{color ? `${color}${variationDesc ? ` - ${variationDesc}` : ''}` : size}</Text>;
}

function markCompareModalPdpNavigation() {
    Storage.local.setItem(Constants.COMPARE_MODAL_OPEN_KEY, {
        returnPath: window.location.pathname
    });
}

function renderAddToBasketButton(product, currentProduct, index, customStyle = {}) {
    if (!product?.skuId) {
        return null;
    }

    return (
        <AddToBasketButton
            sku={product}
            product={currentProduct}
            productId={currentProduct.productId}
            dataAt={`compare_modal_add_to_basket_${index + 1}`}
            variant='special'
            size='sm'
            customStyle={customStyle}
            componentName='compareModal'
            isChooseOptionsModal
        />
    );
}

const ProductCardItem = ({
    product, currentProduct, index, isCompact, getText, handleProductRemove
}) => {
    const targetUrl = currentProduct?.productId ? `/product/${currentProduct?.productId}?skuId=${product?.skuId}` : null;
    const imageProps = {
        size: CARD_IMG_SIZE_GRID_XX_LARGE,
        src: product?.primaryImages?.[0]?.baseUrl
    };

    return (
        <>
            <Box
                css={isCompact ? styles.productCardZoneCompact : styles.productCardZone}
                href={targetUrl}
                onClick={targetUrl ? markCompareModalPdpNavigation : undefined}
                aria-label={`${getText('openProductDetails')} ${currentProduct?.name}`}
            >
                {!isCompact && (
                    <Button
                        css={styles.closeButton}
                        onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleProductRemove(currentProduct?.productId);
                        }}
                        data-at={Sephora.debug.dataAt(`compare_modal_remove_product_${index + 1}`)}
                        aria-label={getText('removeProduct')}
                    >
                        <Icon
                            name='x'
                            size={14}
                        />
                    </Button>
                )}
                <Box css={isCompact ? styles.imageContainerCompact : styles.imageContainer}>
                    <ProductImage {...imageProps} />
                </Box>
                <Box css={isCompact ? styles.productInfoCompact : styles.productInfo}>
                    <Box css={styles.productDetails}>
                        {currentProduct?.brand?.displayName && <Text css={styles.brandName}>{currentProduct?.brand?.displayName}</Text>}
                        {currentProduct?.name && (
                            <Text
                                css={isCompact ? styles.displayNameCompact : styles.displayName}
                                numberOfLines={isCompact ? 1 : 2}
                            >
                                {currentProduct?.name}
                            </Text>
                        )}
                        <VariationDetails
                            product={product}
                            extraCss={isCompact ? styles.variationDetailsCompact : {}}
                        />
                        {product?.skuPrices[0] && (
                            <Text css={styles.price}>{appendDollarSign(formatDiscountPrice(product?.skuPrices[0].price))}</Text>
                        )}
                    </Box>
                    {!isCompact && (
                        <Box>
                            {product?.skuId && <Box css={styles.addToBasketContainer}>{renderAddToBasketButton(product, currentProduct, index)}</Box>}
                        </Box>
                    )}
                </Box>
            </Box>
            {isCompact && (
                <Box>
                    {product?.skuId && (
                        <Box css={styles.addToBasketContainer}>
                            {renderAddToBasketButton(product, currentProduct, index, styles.addToBasketCompact)}
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};

const styles = {
    productCardZone: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    productCardZoneCompact: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'row',
        gap: `${space[1]}px`,
        height: '100%',
        alignItems: 'left',
        justifyContent: 'left',
        transition: 'gap 0.3s ease, height 0.3s ease'
    },
    closeButton: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: '14px',
        height: '14px',
        minWidth: '14px',
        minHeight: '14px',
        padding: 0,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
        '&:hover': {
            opacity: 0.7
        }
    },
    imageContainer: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        transition: 'all 0.3s ease'
    },
    imageContainerCompact: {
        width: '60px',
        height: '60px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexShrink: 0,
        transition: 'width 0.3s ease, height 0.3s ease',
        '& img': {
            transition: 'width 0.3s ease, height 0.3s ease',
            width: '100%',
            height: '100%',
            objectFit: 'cover'
        }
    },
    productInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: `${space[1]}px`,
        paddingX: `${space[2]}px`,
        paddingBottom: `${space[2]}px`,
        flex: 1,
        justifyContent: 'space-between'
    },
    productDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: `${space[1]}px`
    },
    variationDetailsCompact: {
        opacity: 0,
        height: 0,
        transition: 'opacity 0.3s ease, height 0.3s ease'
    },
    brandName: {
        fontSize: `${fontSizes.xs}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        color: colors.black,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    displayName: {
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.normal,
        lineHeight: lineHeights.tight,
        color: colors.black,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    displayNameCompact: {
        whiteSpace: 'no-wrap',
        fontSize: `${fontSizes.sm}px`,
        fontWeight: fontWeights.normal,
        lineHeight: lineHeights.tight,
        color: colors.black,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'all 0.3s ease'
    },
    price: {
        fontSize: `${fontSizes.base}px`,
        fontWeight: fontWeights.bold,
        lineHeight: lineHeights.tight,
        color: colors.black
    },
    addToBasketContainer: {
        width: '100%',
        marginTop: `${space[1]}px`
    },
    addToBasketCompact: {
        width: '50%',
        transition: 'width 0.3s ease, margin-top 0.3s ease'
    },
    size: {
        fontSize: `${fontSizes.sm}px`,
        color: colors.gray
    }
};

ProductCardItem.propTypes = {
    product: PropTypes.object.isRequired,
    currentProduct: PropTypes.object,
    index: PropTypes.number.isRequired,
    isCompact: PropTypes.bool,
    getText: PropTypes.func.isRequired,
    handleProductRemove: PropTypes.func.isRequired
};

export default wrapFunctionalComponent(ProductCardItem, 'ProductCardItem');
